// src/components/rubrica/ContactForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Box
} from '@chakra-ui/react';
import { FiSave, FiX } from 'react-icons/fi';

const ContactForm = ({ isOpen, onClose, contact, onSave, loading, botId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    profile: '',
    behavior: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        profile: contact.profile || '',
        behavior: contact.behavior || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        profile: '',
        behavior: ''
      });
    }
    setErrors({});
  }, [contact, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.email && !formData.phone) {
      newErrors.general = 'Either email or phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const url = contact 
        ? `85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/contacts/${contact.id}`
        : `85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/contacts`;
      
      const method = contact ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.status === 'success') {
        onSave(data.contact);
        onClose();
      } else {
        setErrors({ general: data.message || 'Failed to save contact' });
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      setErrors({ general: 'Network error. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear general error if user provides email or phone
    if ((field === 'email' || field === 'phone') && value && errors.general) {
      setErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setErrors({});
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnOverlayClick={!loading}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            {contact ? 'Edit Contact' : 'Add New Contact'}
          </ModalHeader>
          <ModalCloseButton isDisabled={loading} />
          
          <ModalBody>
            <VStack spacing={4}>
              {errors.general && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  {errors.general}
                </Alert>
              )}

              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  placeholder="Contact name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  isDisabled={loading}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4} w="full">
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="contact@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    isDisabled={loading}
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.phone}>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    type="tel"
                    placeholder="+1 234 567 8900"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    isDisabled={loading}
                  />
                  <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Company</FormLabel>
                <Input
                  placeholder="Company name"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  isDisabled={loading}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Profile</FormLabel>
                <Textarea
                  placeholder="Contact profile information for bot interactions..."
                  value={formData.profile}
                  onChange={(e) => handleChange('profile', e.target.value)}
                  isDisabled={loading}
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Behavior Notes</FormLabel>
                <Textarea
                  placeholder="Notes about how this contact behaves or prefers to interact..."
                  value={formData.behavior}
                  onChange={(e) => handleChange('behavior', e.target.value)}
                  isDisabled={loading}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                leftIcon={<Box as={FiX} />}
                onClick={handleClose}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Box as={FiSave} />}
                isLoading={loading}
                loadingText={contact ? 'Updating...' : 'Creating...'}
              >
                {contact ? 'Update Contact' : 'Create Contact'}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ContactForm;